/**
 * Christmas clipart — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasClipartPack: ToilePack = {
    slug: 'christmas-clipart',
    category: 'season',
    name: 'Christmas clipart',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'holly', label: 'Holly sprig', subject: 'a sprig of holly with three glossy leaves and a cluster of red berries'},
        {key: 'bell', label: 'Bell', subject: 'a golden bell with a red ribbon bow tied at the top and a small clapper showing'},
        {key: 'candycane', label: 'Candy cane', subject: 'a red-and-white striped candy cane with a green ribbon'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['a farm animal instead', 'for a school newsletter', 'as a matching set of three'],
    directionHint: 'try: a farm animal instead',
    seo: {
        title: 'Christmas Clipart: Holly, Bell & Candy Cane',
        metaDescription: 'Nine Christmas clip art designs — a holly sprig, a ribboned bell, and a candy cane — isolated and ready to drop onto a page, three finishes.',
        h1: 'Christmas clipart: holly sprig, bell, candy cane',
        tagline: 'Nine designs, three December staples, three finishes each — isolated, ready to drop onto a page.',
        intro: 'Three Christmas clip art designs — a holly sprig, a golden bell with a ribbon, and a candy cane — each isolated as a single shape, in Scandi folk, vintage card, and cozy cartoon finishes. Ask for a farm animal instead, a school-newsletter set, or a matching trio, and the same clipart grammar carries it.',
        useCases: [
            'a December newsletter graphic',
            'a printed card or invitation accent',
            'a classroom bulletin board',
            'a social media holiday post',
        ],
        faq: [
            {q: 'Is the background transparent, or a solid color?', a: 'Each design sits on a solid background as rendered; for a transparent PNG, remove the background yourself in an image editor.'},
            {q: 'What size do these render at?', a: 'A square format that works at newsletter thumbnail size or as a full-page graphic.'},
            {q: 'Can I get a farm animal instead, or a matching trio?', a: 'Yes — the direction field takes any variation, including a different object or a matching set.'},
            {q: 'What are the three finishes?', a: 'Scandi folk (flat simplified shapes), vintage card (soft halftone grain), and cozy cartoon (rounded warm outlines).'},
        ],
    },
}
